import type { FetchOptions } from 'ofetch'
import type { PendingPublishCache } from '~/composables/usePendingPublish'
import type { StoreAdminAccessMode } from '~/composables/useStoreAdminAccess'
import { applyStoreAdminAccessFromRoute, storeAdminAccessHeader } from '~/composables/useStoreAdminAccess'
import { markStorefrontRateLimited } from '~/composables/useStorefrontHost'
import { requestHostname, shouldSecureAuthCookie } from '~/utils/authCookie'
import { captureApiInitiator, traceLaravelApi } from '~/utils/apiTrace'
import { beginSessionReauth, sessionLoginOffer } from '~/utils/sessionReauth'

const TOKEN_KEY = 'kawaderna_token'

/** Never share the JS auth cookie with `{store}.vntru.com`. */
function hostOnlyAuthCookieDomain(raw: string, storeRoot: string): string {
  const domain = raw.trim()
  if (!domain) return ''
  const normalized = domain.replace(/^\./, '').toLowerCase()
  const root = storeRoot.replace(/^\./, '').toLowerCase() || 'vntru.com'
  if (normalized === root) return ''
  return domain
}

/**
 * Error thrown for any non-2xx API response. Carries the backend's stable
 * machine `code` (e.g. `invalidCredentials`, `phoneExists`) plus validation
 * messages so the UI can react precisely.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null = null,
    readonly errors: Record<string, string[]> = {},
    readonly missing: string[] = [],
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type ApiFetchOptions = FetchOptions & { retries?: number }

const RETRY_DELAY_MS = 1500

/** Creator-side storefront writes, e.g. `/online-stores/12/products/5`. */
const STOREFRONT_WRITE = /^\/online-stores\/([^/?#]+)(?:\/|$)/

/**
 * Writes under a store that never land in its draft: the publish actions
 * themselves, plus the customer records the backend always reads live.
 */
const NOT_A_DRAFT_WRITE = /\/(publish|unpublish|discard-draft|pending-publish|drafts|orders|subscribers|contact-submissions|store-chats|store-chat|chat-shortcuts|ai)(\/|$)/

/**
 * Every design-studio save lands in the storefront draft, so the publish
 * button's pending dot lights up as soon as one succeeds. The next refresh
 * replaces this guess with the server's grouped summary.
 */
function trackStorefrontDraftChange(
  cache: PendingPublishCache,
  path: string,
  method: string,
): void {
  if (!import.meta.client) return
  if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) return

  const route = path.split('?')[0] ?? ''
  const [, storeId] = STOREFRONT_WRITE.exec(route) ?? []
  if (!storeId || NOT_A_DRAFT_WRITE.test(route)) return

  markStoreDirty(
    cache,
    decodeURIComponent(storeId),
    path.includes('/products') || path.includes('/variant-options')
      ? 'products'
      : path.includes('/collections')
        ? 'collections'
        : path.includes('/pages')
          ? 'pages'
          : 'design',
  )
}

/**
 * Drop leftover localStorage tokens from older builds. Persistence is the
 * first-party session cookie on this origin (needed because the API often
 * lives on another host/port, so Laravel's HttpOnly cookie is third-party
 * and is not sent after a reload).
 */
function readLegacyTokenStorage(): string | null {
  if (!import.meta.client) return null
  try {
    const value = localStorage.getItem(TOKEN_KEY)?.trim()
    return value || null
  }
  catch {
    return null
  }
}

function purgeLegacyTokenStorage(): void {
  if (!import.meta.client) return
  try {
    localStorage.removeItem(TOKEN_KEY)
  }
  catch {
    // Ignore — nothing more we can do.
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function applyStoreAdminAccessIfSafe(storeAdminAccess: Ref<StoreAdminAccessMode>) {
  const nuxtApp = tryUseNuxtApp()
  // `useRoute()` inside route middleware reads the outgoing page, not `to`.
  if (!nuxtApp || nuxtApp._processingMiddleware) return
  try {
    applyStoreAdminAccessFromRoute(useRoute(), storeAdminAccess)
  }
  catch {
    // Plugins and non-route callers still share the same access state.
  }
}

const GENERIC_API_MESSAGE = 'Something went wrong. Please try again.'

/** True when a backend/message payload looks like an internal SQL or framework dump. */
export function isInternalErrorMessage(message: string): boolean {
  return /SQLSTATE|SQL:|Illuminate\\|stack trace|Connection:|Unknown column|QueryException|\\vendor\\laravel/i.test(message)
}

function sanitizeApiMessage(status: number, message: string | undefined): string {
  const text = (message ?? '').trim()
  if (status === 401 && /unauthenticated/i.test(text)) {
    return ''
  }
  if (!text || status >= 500 || isInternalErrorMessage(text)) {
    return GENERIC_API_MESSAGE
  }
  return text
}

/** A rejected session, not a wrong password on the sign-in form. */
export function isUnauthenticatedError(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 401) return false
  if (error.code === 'invalidCredentials' || error.code === 'accountNotFound' || error.code === 'invalidPhone') return false
  return true
}

/** Pause on one blocking sign-in popup, shared by every 401 in flight. */
async function waitForSessionLogin(): Promise<boolean> {
  const { promise, alreadyWaiting } = beginSessionReauth()
  if (!alreadyWaiting && import.meta.client) {
    try {
      useState('auth-session-invalidated', () => false).value = true
      useAuthModal().openAuth('signin', { blocking: true })
    }
    catch {
      // The wait still resolves when login stores a new token.
    }
  }
  return promise
}

/** True when the API rejected the caller with HTTP 429 Too Many Requests. */
export function isRateLimitedError(error: unknown): boolean {
  if (error instanceof ApiError) return error.status === 429
  if (!error || typeof error !== 'object') return false
  const status = Number(
    ('status' in error ? error.status : null)
    ?? ('statusCode' in error ? error.statusCode : null)
    ?? 0,
  )
  return status === 429
}

/** True when the API answered that this caller may not read the resource (401/403/404). */
export function isAccessDeniedError(error: unknown): boolean {
  const status = error instanceof ApiError ? error.status : 0
  return status === 401 || status === 403 || status === 404
}

/** True when the browser never received an HTTP response (offline, timeout, in-app browser). */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.status !== 0 || error.code) {
      return false
    }
  }
  else if (!(error instanceof Error)) {
    return false
  }

  const msg = error.message.toLowerCase()
  return msg.includes('failed to fetch')
    || msg.includes('fetch failed')
    || msg.includes('network')
    || msg.includes('no response')
}

/**
 * Resolve the Laravel API base URL for the current runtime.
 *
 * On the client, a relative base like `/api` is ideal (same-origin). During
 * SSR, `$fetch` would resolve that against the Nitro server itself and 404 —
 * so the server must use an absolute internal/public API URL instead.
 */
function joinApiUrl(base: string, path: string): string {
  if (/^https?:\/\//i.test(path)) return path
  const root = base.replace(/\/$/, '')
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${root}${suffix}`
}

const LARAVEL_LOOPBACK_API = 'http://127.0.0.1:8002/api'

function isLocalDevHostname(hostname: string): boolean {
  const host = hostname.toLowerCase()
  return host === 'localhost'
    || host === '127.0.0.1'
    || host.endsWith('.localhost')
}

/**
 * Absolute Laravel URL for SSR. Live hosts (`shop.vntru.com`) stay as-is.
 * Only `{shop}.localhost` is rewritten — Node on Windows cannot resolve it.
 */
function serverLaravelUrl(url: string, internalBase: string): string {
  const fallback = /^https?:\/\//i.test(internalBase)
    ? internalBase.replace(/\/$/, '')
    : LARAVEL_LOOPBACK_API
  const resolved = new URL(url, `${fallback}/`)
  if (!isLocalDevHostname(resolved.hostname)) {
    return resolved.toString()
  }
  const path = `${resolved.pathname}${resolved.search}`
  const apiPath = path === '/api' || path.startsWith('/api/')
    ? (path.slice(4) || '/')
    : path
  return joinApiUrl(fallback, apiPath)
}

type LaravelFetchError = Error & {
  statusCode: number
  data: Record<string, unknown>
  response: { status: number, _data: Record<string, unknown> }
}

/**
 * Loopback fetch that does not inherit the incoming page URL.
 *
 * Nuxt's `$fetch` / ofetch alias is request-bound. During SSR of
 * `{shop}.localhost` it would call `http://shop.localhost/api/...`, and Node
 * on Windows cannot resolve that name even though the browser can.
 */
async function serverLaravelFetch<T>(url: string, options: FetchOptions): Promise<T> {
  const method = String(options.method ?? 'GET').toUpperCase()
  const headers = new Headers()
  const rawHeaders = (options.headers || {}) as Record<string, string | number | undefined>
  for (const [key, value] of Object.entries(rawHeaders)) {
    if (value != null && value !== '') headers.set(key, String(value))
  }

  const init: RequestInit = {
    method,
    headers,
    redirect: 'manual',
  }
  const timeoutMs = typeof options.timeout === 'number' ? options.timeout : 12_000
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    init.signal = AbortSignal.timeout(timeoutMs)
  }

  if (options.body != null && method !== 'GET' && method !== 'HEAD') {
    if (typeof options.body === 'string' || options.body instanceof FormData) {
      init.body = options.body as BodyInit
    }
    else {
      if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
      init.body = JSON.stringify(options.body)
    }
  }

  const res = await globalThis.fetch(url, init)
  const text = await res.text()
  let data: Record<string, unknown> = {}
  if (text) {
    try {
      data = JSON.parse(text) as Record<string, unknown>
    }
    catch {
      data = { message: text }
    }
  }

  if (!res.ok) {
    const err = new Error(
      typeof data.message === 'string' ? data.message : (res.statusText || 'Request failed'),
    ) as LaravelFetchError
    err.statusCode = res.status
    err.data = data
    err.response = { status: res.status, _data: data }
    throw err
  }

  return data as T
}

function resolveApiBase(config: ReturnType<typeof useRuntimeConfig>): string {
  let publicBase = String(config.public.apiBase || '').replace(/\/$/, '')
  // An open tab can keep a stale runtime config from when .env pointed at
  // :8001. Those ports are not Laravel; send the browser through the Nuxt proxy.
  if (import.meta.client && /^https?:\/\/(localhost|127\.0\.0\.1):(8000|8001|8081)(\/|$)/.test(publicBase)) {
    publicBase = '/api'
  }
  if (import.meta.server) {
    const internal = String(config.apiInternalBase || '').replace(/\/$/, '')
    if (internal) return internal
    if (/^https?:\/\//i.test(publicBase)) return publicBase
    const siteUrl = String(config.public.siteUrl || '').replace(/\/$/, '')
    if (siteUrl && publicBase.startsWith('/')) {
      return `${siteUrl}${publicBase}`
    }
    // Last resort: never let ofetch resolve `/api` against `{shop}.localhost`.
    // Node on Windows cannot look that name up (ENOTFOUND); browsers can.
    return 'http://127.0.0.1:8002/api'
  }
  return publicBase
}

/**
 * Thin wrapper around `$fetch` that targets the Laravel API: prefixes the
 * base URL, attaches the bearer token, requests JSON, and normalises errors
 * into {@link ApiError}. The token is kept in memory and a first-party cookie
 * on this origin so reloads and SSR still authenticate. It is not written to
 * localStorage.
 */
export function useApi() {
  const config = useRuntimeConfig()
  const token = useState<string | null>('auth-token', () => null)
  // Resolved here, not inside `apiFetch`: the draft tracker runs after a fetch
  // resolves, where the Nuxt instance `useState` needs is no longer available.
  const pendingPublish = usePendingPublishCache()
  const storeAdminAccess = useState<StoreAdminAccessMode>('store-admin-access', () => null)
  const langState = useState<string | null>('kawaderna-lang', () => null)
  applyStoreAdminAccessIfSafe(storeAdminAccess)
  const cookieDomain = hostOnlyAuthCookieDomain(
    String(config.public.cookieDomain || '').trim(),
    String(config.public.storeRootDomain || ''),
  )
  const onStorefront = Boolean(currentStoreSubdomain())
  const tokenCookie = useSharedCookie<string | null>(TOKEN_KEY, {
    maxAge: 60 * 60 * 24 * 30,
    sameSite: 'lax',
    path: '/',
    ...(cookieDomain ? { domain: cookieDomain } : {}),
    ...(shouldSecureAuthCookie({
      dev: import.meta.dev,
      hostname: requestHostname(),
      cookieDomain,
      siteUrl: String(config.public.siteUrl || ''),
    }) ? { secure: true } : {}),
  })

  function loadToken(): string | null {
    if (token.value) {
      return token.value
    }

    if (tokenCookie.value) {
      token.value = tokenCookie.value
      return token.value
    }

    // Flutter's studio WebView (and older builds) write the bearer here.
    // Adopt it into the first-party cookie, then drop the leftover.
    const legacy = readLegacyTokenStorage()
    if (legacy) {
      setToken(legacy)
      purgeLegacyTokenStorage()
      return token.value
    }

    return null
  }

  function setToken(value: string): void {
    token.value = value
    if (import.meta.client) {
      tokenCookie.value = value
    }
  }

  function clearToken(): void {
    token.value = null
    if (import.meta.client) {
      tokenCookie.value = null
    }
    purgeLegacyTokenStorage()
  }

  async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
    applyStoreAdminAccessIfSafe(storeAdminAccess)
    const { retries = 0, ...fetchOptions } = options
    const baseURL = resolveApiBase(config)
    const method = String(fetchOptions.method ?? 'GET')
    const initiator = captureApiInitiator()
    let lastError: unknown
    let reauthUsed = false

    for (let attempt = 0; attempt <= retries; attempt++) {
      // Reload after a sign-in popup replaces an expired token.
      const bearer = loadToken()
      try {
        const adminHeader = storeAdminAccessHeader(storeAdminAccess.value)
        const lang = langState.value
        const request = {
          timeout: import.meta.server ? 12_000 : 30_000,
          ...fetchOptions,
          // Store subdomains omit cookies so a shop cannot ride the platform
          // session. The first-party app still sends the session cookie on
          // `/public/*`: web login is cookie-only, and without it an owner
          // is a guest and an unpublished store 404s.
          credentials: onStorefront ? 'omit' : 'include',
          headers: {
            Accept: 'application/json',
            // Send the bearer on public storefront routes too. Owners (and
            // design collaborators) must authenticate so `/api/public/*` can
            // resolve the studio draft — otherwise product cards keep serving
            // the last published images (often deleted wizard demos) while
            // the PDP, which hits `/online-stores/...`, shows the real photos.
            ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
            ...(adminHeader ? { 'X-Store-Admin-Access': adminHeader } : {}),
            ...(lang ? { 'Accept-Language': String(lang) } : {}),
            ...(fetchOptions.headers as Record<string, string> | undefined),
          },
          onResponse({ response }) {
            traceLaravelApi(method, path, response.status, initiator, response.headers)
          },
          onResponseError({ response }) {
            traceLaravelApi(method, path, response.status, initiator, response.headers)
          },
        } satisfies FetchOptions
        // SSR `$fetch` is request-bound: a relative `/api` on `shop.localhost`
        // becomes `http://shop.localhost/api/...`, which Node cannot resolve
        // on Windows. Use undici/global fetch against the loopback API instead.
        const result = import.meta.server
          ? await serverLaravelFetch<T>(serverLaravelUrl(joinApiUrl(baseURL, path), baseURL), request)
          : await $fetch<T>(path, { baseURL, ...request })

        if (import.meta.server) {
          traceLaravelApi(method, path, 200, initiator)
        }

        trackStorefrontDraftChange(pendingPublish, path, String(fetchOptions.method ?? 'GET'))

        return result
      }
      catch (error: unknown) {
        const err = error as {
          response?: { status?: number, _data?: Record<string, unknown> }
          statusCode?: number
          data?: Record<string, unknown>
          message?: string
        }
        const status = err?.response?.status ?? err?.statusCode ?? 0
        const data = err?.response?._data ?? err?.data ?? {}
        const rawMessage = typeof data?.message === 'string'
          ? data.message
          : (typeof err?.message === 'string' ? err.message : 'Request failed')

        lastError = new ApiError(
          sanitizeApiMessage(status, rawMessage),
          status,
          typeof data?.error === 'string' ? data.error : null,
          (data?.errors && typeof data.errors === 'object' ? data.errors : {}) as Record<string, string[]>,
          Array.isArray(data?.missing) ? data.missing as string[] : [],
        )

        if (status === 429 && String(path).includes('/public/online-stores')) {
          markStorefrontRateLimited()
        }

        const offer = lastError instanceof ApiError
          ? sessionLoginOffer({
            status: lastError.status,
            code: lastError.code,
            path: String(path),
          })
          : 'none'

        // Guest or expired session: stay on this page, one popup, then retry.
        // 500 and network failures fall through and are not treated as logout.
        if (!reauthUsed && offer === 'blocking' && import.meta.client) {
          reauthUsed = true
          clearToken()
          const restored = await waitForSessionLogin()
          if (restored) {
            attempt -= 1
            continue
          }
        }

        if (attempt < retries && lastError instanceof ApiError && lastError.status === 0 && isNetworkError(lastError)) {
          await sleep(RETRY_DELAY_MS * (attempt + 1))
          continue
        }

        throw lastError
      }
    }

    throw lastError
  }

  /**
   * Multipart POST with upload progress. `fetch` cannot report bytes sent, so
   * the browser path uses XMLHttpRequest with the same base URL, headers and
   * {@link ApiError} shape as {@link apiFetch}. Progress stops at 99 until the
   * API has answered, so 100% means the file is actually stored.
   */
  function apiUpload<T>(
    path: string,
    body: FormData,
    options: { onProgress?: (percent: number) => void, timeout?: number } = {},
  ): Promise<T> {
    if (import.meta.server || typeof XMLHttpRequest === 'undefined') {
      return apiFetch<T>(path, { method: 'POST', body, timeout: options.timeout })
    }

    applyStoreAdminAccessIfSafe(storeAdminAccess)
    const url = joinApiUrl(resolveApiBase(config), path)
    const bearer = loadToken()
    const adminHeader = storeAdminAccessHeader(storeAdminAccess.value)
    const lang = langState.value
    const initiator = captureApiInitiator()
    const report = options.onProgress

    return new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', url)
      xhr.withCredentials = !onStorefront
      xhr.timeout = options.timeout ?? 30_000
      xhr.setRequestHeader('Accept', 'application/json')
      if (bearer) xhr.setRequestHeader('Authorization', `Bearer ${bearer}`)
      if (adminHeader) xhr.setRequestHeader('X-Store-Admin-Access', adminHeader)
      if (lang) xhr.setRequestHeader('Accept-Language', String(lang))

      xhr.upload.onprogress = (event) => {
        if (!report || !event.lengthComputable || event.total <= 0) return
        report(Math.min(99, Math.floor((event.loaded / event.total) * 100)))
      }

      xhr.onload = () => {
        traceLaravelApi('POST', path, xhr.status, initiator)
        let data: Record<string, unknown> = {}
        if (xhr.responseText) {
          try {
            data = JSON.parse(xhr.responseText) as Record<string, unknown>
          }
          catch {
            data = { message: xhr.responseText }
          }
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          report?.(100)
          trackStorefrontDraftChange(pendingPublish, path, 'POST')
          resolve(data as T)
          return
        }

        reject(new ApiError(
          sanitizeApiMessage(xhr.status, typeof data.message === 'string' ? data.message : xhr.statusText),
          xhr.status,
          typeof data.error === 'string' ? data.error : null,
          (data.errors && typeof data.errors === 'object' ? data.errors : {}) as Record<string, string[]>,
          Array.isArray(data.missing) ? data.missing as string[] : [],
        ))
      }
      xhr.onerror = () => reject(new ApiError('Network request failed', 0))
      xhr.ontimeout = () => reject(new ApiError('Network request timed out', 0))
      xhr.send(body)
    })
  }

  return { apiFetch, apiUpload, token, loadToken, setToken, clearToken }
}
